/**
 * Shared pure helpers, defaults, and prop-shapes for chart-case components.
 * No JSX here — see `parts.tsx` for the shared cartesian JSX blocks.
 */
import type { ChartDatapointSelection } from '@/types/reports';

import type {
  AxisConfig,
  BarChartOptions,
  ChartConfiguration,
  LineChartOptions,
  PieChartOptions,
} from '../types';
import type { computeValueAxisMeta } from '../utils/chartAxisMeta';
import { WRAPPED_TICK_LINE_HEIGHT } from './CategoryAxisTick';

/**
 * Fallback series color when a palette token can't be resolved (e.g. a unit
 * test environment without the theme stylesheet). Brand-500.
 */
export const FALLBACK_SERIES_COLOR = '#3528B8';

/**
 * Spread onto every series element (`Bar`, `Line`, `Area`, `Pie`) to turn
 * Recharts' enter/update animation off.
 *
 * This is not a style preference — the animation as Recharts 3.x implements
 * it cannot do what it looks like it does. `useAnimationId` decides whether
 * to re-animate by comparing the series' **props object by reference**, and
 * React allocates a fresh props object on every render, so the check is
 * always true: every render mints a new animation id and restarts the
 * animation from the previous geometry.
 *
 * A resize is the case where that becomes visible. `ResponsiveContainer`
 * applies no debounce by default, so dragging an edge re-renders on every
 * frame, and each frame restarts a 400ms (Bar) or 1500ms (Line/Area/Pie)
 * interpolation. The marks never reach their target before the next frame
 * moves it, so the chart trails the container the whole way down and only
 * catches up once the drag stops. The same restart fires on a filter change,
 * a drill-panel open and a focus-mode open — anywhere a tile re-renders.
 *
 * Turning it off makes geometry track its container exactly, which is what a
 * dense dashboard wants: fourteen tiles re-animating on every filter change
 * was costing legibility and conveying nothing about the data. It also means
 * we respect reduced-motion preferences here by construction.
 */
export const CHART_SERIES_ANIMATION = { isAnimationActive: false } as const;

/**
 * Resolve a CSS custom property to its runtime value. Recharts renders SVG
 * attributes, which don't resolve var() references, so token colors must be
 * looked up at render time (this also makes them dark-mode aware).
 */
export function resolveCssColor(varName: string): string {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
}

/** sRGB channel -> linear light, per WCAG 2.x relative luminance. */
function channelToLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/**
 * Parse the color forms a resolved series color can actually take: the
 * `rgb(r g b)` / `rgb(r, g, b)` that `getComputedStyle` returns for our
 * tokens, and the `#rrggbb` / `#rgb` of {@link FALLBACK_SERIES_COLOR} and any
 * user-supplied `customColors`. Returns null for anything else rather than
 * guessing, so callers can fall back to a fixed label color.
 */
function parseRgb(color: string): [number, number, number] | null {
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
  if (hex) {
    const digits = hex[1];
    const full =
      digits.length === 3
        ? digits
            .split('')
            .map(d => d + d)
            .join('')
        : digits;
    return [
      parseInt(full.slice(0, 2), 16),
      parseInt(full.slice(2, 4), 16),
      parseInt(full.slice(4, 6), 16),
    ];
  }
  // Only an explicit rgb()/rgba() is read as channels. Pulling the first three
  // numbers out of ANY string misread `hsl(210 50% 40%)` or `oklch(…)` as
  // rgb(210, 50, 40), and the wrong luminance then recoloured the funnel ramp
  // and flipped its labels. Other valid CSS colours return null, the
  // documented "can't tell" answer.
  const rgbMatch = /^rgba?\(([^)]*)\)$/i.exec(color.trim());
  if (!rgbMatch) {
    return null;
  }
  const nums = rgbMatch[1].match(/-?[\d.]+/g);
  if (!nums || nums.length < 3) {
    return null;
  }
  const [r, g, b] = nums.slice(0, 3).map(Number);
  if ([r, g, b].some(n => !Number.isFinite(n))) {
    return null;
  }
  return [r, g, b];
}

/**
 * Pick the label color that stays readable on top of an arbitrary series
 * fill, as a Tailwind class.
 *
 * A label sitting ON a data-driven fill can't take a theme text token: the
 * fill is the same color in both themes, so the label must be too — a token
 * would invert underneath it and fail in whichever theme it wasn't checked
 * in. Black and white are the literal choices here, and which one wins is a
 * property of the fill, not of the theme.
 *
 * Always-white (the previous behaviour) clears 4.5:1 on exactly one of the
 * eight palette slots; choosing per fill clears it on all sixteen values
 * across both themes. 4.5:1 rather than 3:1 because these labels are 14px
 * semibold, which is under WCAG's large-text threshold.
 */
export function readableTextClassOn(color: string): string {
  const rgb = parseRgb(color);
  if (!rgb) {
    return 'text-white';
  }
  const [r, g, b] = rgb;
  const luminance =
    0.2126 * channelToLinear(r) +
    0.7152 * channelToLinear(g) +
    0.0722 * channelToLinear(b);
  // Contrast vs white is (1.05)/(L+0.05); vs black it is (L+0.05)/0.05.
  // They cross at L = sqrt(1.05 * 0.05) - 0.05 ≈ 0.1791.
  return luminance > 0.1791 ? 'text-black' : 'text-white';
}

/**
 * Total distance the last step travels from the base, as a fraction of the
 * way to white (or to black — see {@link sequentialStepColors}).
 *
 * The ENDPOINT is fixed rather than the per-step increment, so a 3-stage and
 * an 8-stage funnel span the same range and read as the same family; only the
 * granularity changes.
 *
 * 0.35 is a balance, not a round number. Measured from chart-1 (#b4a2db, the
 * usual base), against the white card:
 *
 *     extent   total ΔL*   tail vs card
 *     0.20      6.2         1.90:1
 *     0.35     10.8         1.67:1
 *     0.50     15.4         1.47:1
 *
 * Going wider grades more obviously but fades the tail, and in a funnel the
 * tail step is also the NARROWEST bar — faint plus narrow is how a final
 * conversion count disappears. Going narrower protects the bar and stops the
 * ramp reading as deliberate, which is the thing being fixed. At 0.35 an
 * 8-step funnel still moves 1.5 L* per step (adjacent touching patches
 * separate at ~1), and the value label inside the palest step still clears
 * 12:1, so the number is never at risk — only the bar's edge softens.
 */
const SEQUENTIAL_RAMP_EXTENT = 0.35;

/**
 * Build an ordered, single-hue ramp of `count` colors from one base fill.
 *
 * For ORDERED steps — funnel stages, and anything else where position carries
 * meaning — a categorical palette is the wrong instrument: it claims each step
 * is a different *thing*, when they are the same measure at successive points.
 * The fix is not to flatten every step to one color either; that is what this
 * funnel did, and a column of identical bars reads as unfinished. A sequential
 * ramp says what is true: one hue (one measure), varying lightness (position).
 * Lightness here reinforces the width, which already encodes the value.
 *
 * The base is always index 0 — the fullest step, matching the sequential
 * convention that darker means more, and the funnel's own widest-first order.
 *
 * The ramp direction is chosen so it always moves AWAY from the black/white
 * label threshold {@link readableTextClassOn} tests: a fill that takes black
 * labels lightens, one that takes white labels darkens. That is the invariant
 * that matters — every step in a funnel keeps the same label color, so the
 * labels can't flip partway down the chart and read as a rendering fault. It
 * holds for a user-supplied `customColors` value too, not just palette slots.
 *
 * Returns `rgb(r g b)` strings. An unparseable base yields `count` copies of
 * it unchanged, so a bad custom color degrades to the old flat behaviour
 * rather than to a broken fill.
 */
export function sequentialStepColors(base: string, count: number): string[] {
  if (count <= 0) {
    return [];
  }
  const rgb = parseRgb(base);
  if (!rgb || count === 1) {
    return Array.from({ length: count }, () => base);
  }
  const towardWhite = readableTextClassOn(base) === 'text-black';
  const target = towardWhite ? 255 : 0;
  return Array.from({ length: count }, (_unused, index) => {
    const t = (index / (count - 1)) * SEQUENTIAL_RAMP_EXTENT;
    const [r, g, b] = rgb.map(channel =>
      Math.round(channel * (1 - t) + target * t)
    );
    return `rgb(${r} ${g} ${b})`;
  });
}

/** Return type of {@link computeValueAxisMeta} — passed as a single prop. */
export type ValueAxisMeta = ReturnType<typeof computeValueAxisMeta>;

/**
 * Map scale values from our config to Recharts values. Pure — hoisted to
 * module scope so chart-case components don't need it threaded as a prop.
 */
export function mapScale(scale: 'linear' | 'logarithmic') {
  return scale === 'logarithmic' ? ('log' as const) : ('linear' as const);
}

/**
 * Format axis labels with units and decimal places. Pure — hoisted to
 * module scope so chart-case components don't need it threaded as a prop.
 */
export function formatAxisValue(
  value: number,
  axisConfig?: AxisConfig
): string {
  if (!axisConfig) {
    return String(value);
  }

  const formatted = value.toFixed(axisConfig.decimalPlaces);

  if (axisConfig.unit) {
    switch (axisConfig.unit) {
      case '$':
        return `$${formatted}`;
      case '%':
        return `${formatted}%`;
      case 'K':
        return `${(value / 1000).toFixed(axisConfig.decimalPlaces)}K`;
      case 'M':
        return `${(value / 1000000).toFixed(axisConfig.decimalPlaces)}M`;
      case 'B':
        return `${(value / 1000000000).toFixed(axisConfig.decimalPlaces)}B`;
      default:
        return formatted;
    }
  }

  return formatted;
}

/**
 * Axis-title props shared by every cartesian chart. The bottom title gets
 * its own band below the ticks (via the XAxis height bump at each call
 * site); the left title runs vertically along the axis. Pure — hoisted to
 * module scope so chart-case components don't need it threaded as a prop.
 */
export function xAxisLabelProps(value: string | undefined) {
  return value
    ? {
        value,
        position: 'insideBottom' as const,
        offset: 0,
        fill: resolveCssColor('--color-text-secondary'),
        fontSize: 12,
      }
    : undefined;
}
export function yAxisLabelProps(value: string | undefined) {
  return value
    ? {
        value,
        angle: -90,
        position: 'insideLeft' as const,
        style: { textAnchor: 'middle' as const },
        fill: resolveCssColor('--color-text-secondary'),
        fontSize: 12,
      }
    : undefined;
}
/**
 * Right-axis mirror of {@link yAxisLabelProps}, for the combo chart's
 * secondary (line) axis. `offset` pushes the title clear of the right
 * axis's own tick labels, which — unlike the left axis's tick labels —
 * render on the same side as an `insideRight` title.
 */
export function yAxisRightLabelProps(value: string | undefined) {
  return value
    ? {
        value,
        angle: 90,
        position: 'insideRight' as const,
        offset: 10,
        style: { textAnchor: 'middle' as const },
        fill: resolveCssColor('--color-text-secondary'),
        fontSize: 12,
      }
    : undefined;
}

/**
 * Maps a line/area series' `lineStyle` option to the recharts curve `type`.
 * Pure — hoisted to module scope so LineChartCase, AreaChartCase, and
 * ComboChartCase share one implementation.
 */
export function curveTypeForLineStyle(
  lineStyle: LineChartOptions['lineStyle']
): 'monotone' | 'step' | 'linear' {
  if (lineStyle === 'smooth') {
    return 'monotone';
  }
  if (lineStyle === 'stepped') {
    return 'step';
  }
  return 'linear';
}

/**
 * Default chart-specific options when a chart has no saved `chartOptions` —
 * hoisted so both the case-local fallbacks and the combo-chart case (which
 * blends a bar group and a line group, and so needs both bar and line
 * defaults at once) share one set of literals. Line and area defaults
 * differ (`lineStyle`, `showPoints`, `areaFill`) — never merge them.
 */
export const DEFAULT_BAR_OPTIONS: BarChartOptions = {
  orientation: 'vertical',
  stacking: 'none',
};
export const DEFAULT_LINE_OPTIONS: LineChartOptions = {
  lineStyle: 'straight',
  showPoints: true,
  areaFill: 'none',
};
export const DEFAULT_AREA_OPTIONS: LineChartOptions = {
  lineStyle: 'smooth',
  showPoints: false,
  areaFill: 'gradient',
};

/**
 * Donut-first defaults for pie charts without saved chartOptions. The old
 * default (flat disc, outside value labels with leader lines) degenerated
 * badly — a single-slice pie rendered as a full circle with a stray label
 * line crossing it. The legend and tooltip carry slice details instead, and
 * the donut center shows the formatted total.
 */
export const DEFAULT_PIE_OPTIONS: PieChartOptions = {
  innerRadius: 62,
  startAngle: 90,
  labelPosition: 'none',
  showPercentages: true,
};

/**
 * Tick text size shared by every cartesian axis. Also the basis for the
 * category-axis band heights below, so a change here moves both together.
 */
export const AXIS_TICK_FONT_SIZE = 12;

/**
 * Category-axis band with no title: recharts' own default, which already fits
 * one line of tick text below the `tickSize + tickMargin` offset.
 */
const CATEGORY_AXIS_HEIGHT = 30;
/**
 * Category-axis band carrying a title below the ticks. One 12px tick line
 * sits 8px below the axis (recharts' `tickSize: 6` + `tickMargin: 2`), the
 * title takes another line, and 2px keeps its descenders off the edge.
 */
const CATEGORY_AXIS_HEIGHT_WITH_TITLE = 40;

/**
 * Height to give a chart's category (x) axis.
 *
 * Shared by every cartesian case so a tile and a faceted panel reserve the
 * same band — the four cases previously each hard-coded the number and drifted
 * apart whenever one was tuned. `height` is explicit regardless of whether
 * labels are shown, so a hidden-label panel reserves the band a labelled one
 * would and the plot areas stay aligned.
 */
export function categoryAxisHeight({
  hasTitle,
  extraLines = 0,
}: {
  hasTitle: boolean;
  /** Additional wrapped tick lines to reserve room for. */
  extraLines?: number;
}): number | undefined {
  if (!hasTitle && extraLines === 0) {
    // Leave it to recharts, which is what every call site did before.
    return undefined;
  }
  const base = hasTitle
    ? CATEGORY_AXIS_HEIGHT_WITH_TITLE
    : CATEGORY_AXIS_HEIGHT;
  return base + extraLines * WRAPPED_TICK_LINE_HEIGHT;
}

/**
 * Plot-area margin for every cartesian chart.
 *
 * Recharts' default (5 on each side) spends width on the left that the value
 * axis already reserves, and leaves the last category label short of room on
 * the right, where a tile's `overflow-hidden` clips it. Zero on the left
 * hands that space to the plot; the right gutter is the half-label overhang
 * of the final category; `bottom` only has to clear the axis title's
 * descenders, since {@link categoryAxisHeight} already reserves its line.
 */
export const CARTESIAN_CHART_MARGIN = {
  top: 4,
  right: 8,
  bottom: 2,
  left: 0,
} as const;

/**
 * Fixed left-axis width for panels in a faceted (multi-unit) cartesian
 * chart, so every panel's plot area lines up under the shared x-axis.
 */
export const FACET_YAXIS_WIDTH = 72;
/** Minimum panel height in a faceted chart, guarding against a near-zero
 * height when many unit groups divide a modest total chart height. */
export const FACET_MIN_PANEL_HEIGHT = 120;

/**
 * Shared prop shape for the cartesian chart-case components (Bar/Line/Area).
 * Panel-layout props (`showXAxisLabels`/`yAxisWidth`/`renderLegend`) let a
 * faceting layer render several panels that share one x-axis band and one
 * plot width without touching the per-chart-type rendering logic. `syncId`
 * lets a faceting layer sync tooltip/cursor hover across panels.
 */
export interface CartesianChartCaseProps {
  chartData: Record<string, unknown>[];
  config: ChartConfiguration;
  columnTitles: Record<string, string>;
  height: number;
  legendAlign: 'left' | 'center' | 'right';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Recharts Formatter type is complex, using any for callback
  tooltipFormatter: (value: any, name: any, entry: any) => any;
  /** The measure keys this component draws. */
  seriesKeys: string[];
  /** Map from measure key to resolved color. */
  seriesColors: Record<string, string>;
  /** Currency/tick/integer-tick/axis-min derivation for the value axis. */
  axisMeta: ValueAxisMeta;
  /** Category-axis (x) title for the standard orientation. */
  categoryBottomLabel?: string;
  /** Value-axis (y) title for the standard orientation. */
  valueAxisTitle?: string;
  /**
   * When false, the x-axis renders with no ticks/labels but keeps the same
   * height it would otherwise use, so plot areas stay identical across
   * panels sharing one category axis. Never use recharts' `hide` for this —
   * a hidden axis contributes zero height and misaligns the plot.
   */
  showXAxisLabels?: boolean;
  /** Explicit value-axis width, for aligning panels sharing one axis. */
  yAxisWidth?: number;
  /** Render the legend only when the caller also wants one for this panel. */
  renderLegend?: boolean;
  /** Shared recharts syncId so hovering one panel syncs the tooltip/cursor
   * across every panel of a faceted chart. */
  syncId?: string;
  /** Called when the user clicks (or keyboard-activates) a chart datapoint. */
  onDatapointSelect?: (sel: ChartDatapointSelection) => void;
  /** The currently active/selected datapoint, used to dim all others. */
  activeSelection?: ChartDatapointSelection | null;
}

/**
 * Prop bag for a value (quantity) `<YAxis>` — shared by every cartesian
 * chart-case's value axis. Returns an OBJECT to spread onto `<YAxis>`
 * (never a JSX element), since some callers (BarChartCase) mutate
 * `dataKey`/`type` on top depending on orientation.
 *
 * `tickFormatter`, `ticks`, and `scale` are always caller-supplied (rather
 * than defaulted here) because at least one call site needs to override
 * them to something falsy/fixed that a `??` default would clobber —
 * BarChartCase's horizontal orientation needs `ticks: undefined` (not the
 * default `axisMeta.smallIntegerTicks`), and the combo chart's right axis
 * hard-codes `scale: 'linear'` regardless of axis config.
 */
export function valueAxisProps({
  axisMeta,
  axisConfig,
  label,
  labelSide = 'left',
  width,
  domainMin = 'auto',
  scale,
  tickFormatter,
  ticks,
  hide,
}: {
  axisMeta: ValueAxisMeta;
  axisConfig?: AxisConfig;
  label?: string;
  /** Which label-props builder to use — the combo chart's right axis needs
   * the right-mirrored variant. */
  labelSide?: 'left' | 'right';
  /**
   * Explicit axis width. Omit to let recharts size the band to the widest
   * tick label plus the title — recharts' fixed 60px default left a visible
   * gutter between a short label ("10") and the plot on every tile, and
   * clipped a wide one ("$1,250K") into it.
   *
   * A caller MUST pass a number when the axis carries *categories* rather
   * than quantities (horizontal bars): auto-sizing to the longest category
   * name would let one label eat half a tile.
   */
  width?: number;
  domainMin?: number | 'auto';
  scale: 'linear' | 'log';
  tickFormatter: ((value: number) => string) | undefined;
  ticks: number[] | undefined;
  /** Explicit hide override — the combo chart's right axis defaults to
   * visible when there's no yAxisRight config at all, unlike the left axis
   * (always present when `config.axes` is set). */
  hide?: boolean;
}) {
  return {
    hide: hide ?? !axisConfig?.show,
    axisLine: false as const,
    tickLine: false as const,
    tick: {
      fill: resolveCssColor('--color-text-secondary'),
      fontSize: AXIS_TICK_FONT_SIZE,
    },
    width: width ?? ('auto' as const),
    label:
      labelSide === 'right'
        ? yAxisRightLabelProps(label)
        : yAxisLabelProps(label),
    domain: [axisConfig?.min ?? domainMin, axisConfig?.max ?? 'auto'] as [
      number | 'auto',
      number | 'auto',
    ],
    scale,
    tickFormatter,
    allowDecimals: !axisMeta.allIntegerValues,
    ticks,
  };
}
